import { useEffect, useRef, useState } from 'react';
import { extractYoutubeId, formatTime, youtubeThumb } from '../utils/misc.js';
import { useStudioStore } from '../stores/useStudioStore.js';
import { Icon } from '../components/Icon.js';
import type { ProjectDoc } from '../types.js';

interface HomeProps {
  onImportInstrumental: (file: File) => Promise<void>;
  onOpenProjects: () => void;
  onInstall: () => void;
}

export function Home({ onImportInstrumental, onOpenProjects, onInstall }: HomeProps) {
  const input = useStudioStore((s) => s.youtubeInput);
  const setInput = useStudioStore((s) => s.setYoutubeInput);
  const previewId = useStudioStore((s) => s.youtubePreviewId);
  const setPreviewId = useStudioStore((s) => s.setYoutubePreviewId);
  const err = useStudioStore((s) => s.youtubeError);
  const setError = useStudioStore((s) => s.setYoutubeError);
  const createProject = useStudioStore((s) => s.createProject);
  const setToast = useStudioStore((s) => s.setToast);
  const installPrompt = useStudioStore((s) => s.installPrompt);
  const [rights, setRights] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [meta, setMeta] = useState<{ title?: string; author_name?: string; thumbnail_url?: string } | null>(null);
  const [heroReady, setHeroReady] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setHeroReady(true);
  }, []);

  useEffect(() => {
    const id = extractYoutubeId(input);
    setPreviewId(id);
    setMeta(null);
    if (!id) return;
    let active = true;
    // oEmbed oficial só para título/canal/thumbnail, nunca para baixar áudio.
    fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(`https://www.youtube.com/watch?v=${id}`)}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => { if (active) setMeta(data); })
      .catch(() => { if (active) setMeta(null); });
    return () => { active = false; };
  }, [input, setPreviewId]);

  const startYoutube = () => {
    const id = extractYoutubeId(input);
    if (!id) {
      setError('Cole um link válido do YouTube (youtube.com, youtu.be ou Shorts).');
      return;
    }
    createProject({
      title: meta?.title ?? 'Novo projeto',
      sourceType: 'youtube',
      youtubeVideoId: id,
      youtubeTitle: meta?.title,
    });
  };

  const startQuick = () => createProject({ title: 'Gravação rápida', sourceType: 'none' });

  const importFile = async (file?: File) => {
    if (!file) return;
    if (!rights) {
      setError('Confirme que você tem autorização para usar este áudio.');
      return;
    }
    setFileBusy(true);
    setError(null);
    try {
      await onImportInstrumental(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível abrir este áudio.');
    } finally {
      setFileBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <main className="home-page">
      <div className="home-noise" />
      <header className="home-header">
        <a className="brand brand-home" href="#top" onClick={(e) => e.preventDefault()} aria-label="Vocal Studio">
          <span className="brand-mark"><Icon name="logo" size={21} /></span>
          <span className="brand-name">VOCAL<span>STUDIO</span></span>
          <span className="brand-version">BETA</span>
        </a>
        <div className="home-header-actions">
          <span className="privacy-pill"><span className="privacy-dot" /> LOCAL & PRIVADO</span>
          <button className="btn btn-quiet header-projects" onClick={onOpenProjects}>
            <Icon name="folder" size={16} /> Meus projetos
          </button>
          {installPrompt && <button className="icon-btn" title="Instalar aplicativo" onClick={onInstall}><Icon name="install" /></button>}
        </div>
      </header>

      <section className={`hero ${heroReady ? 'hero-ready' : ''}`} id="top">
        <div className="hero-copy">
          <div className="eyebrow"><span className="eyebrow-line" /> GRAVE A SUA VOZ. DO SEU JEITO.</div>
          <h1>Seu próximo take<br /><span>começa aqui.</span></h1>
          <p>Escolha uma música, conecte os fones e cante. Um estúdio vocal simples, no navegador — sem enviar sua gravação para a nuvem.</p>
          <div className="hero-benefits">
            <span><Icon name="shield" size={14} /> Áudio local</span>
            <span><Icon name="bolt" size={14} /> Sem instalação</span>
            <span><Icon name="wave" size={14} /> Fácil de usar</span>
          </div>
          <div className="hero-stamp"><span className="stamp-ring"><Icon name="wave" size={22} /></span><span>UM ESTÚDIO<br />NO SEU BROWSER</span></div>
        </div>
        <div className="hero-art" aria-hidden="true">
          <div className="hero-orbit orbit-one" /><div className="hero-orbit orbit-two" />
          <div className="hero-disc"><div className="disc-groove groove-a" /><div className="disc-groove groove-b" /><div className="disc-label"><Icon name="logo" size={35} /><span>TAKE<br />01</span></div><div className="disc-hole" /></div>
          <div className="hero-meter"><span /><span /><span /><span /><span /><span /><span /><span /><span /></div>
          <div className="hero-art-tag">REC <span /></div>
        </div>
      </section>

      <section className="start-section" aria-label="Começar gravação">
        <div className="section-heading">
          <div><span className="section-kicker">ESCOLHA COMO COMEÇAR</span><h2>Vamos gravar?</h2></div>
          <span className="section-hint">3 passos. Sem complicação.</span>
        </div>

        <div className="start-grid">
          <article className="start-card youtube-card">
            <div className="card-topline"><span className="card-icon youtube-icon"><Icon name="youtube" size={20} /></span><span className="card-step">01 / YOUTUBE KARAOKE</span></div>
            <h3>Cante com um vídeo</h3>
            <p>Use o player oficial do YouTube como referência. Grave sua voz sincronizada e baixe o vocal.</p>
            <label htmlFor="youtube-link" className="input-label">LINK DA MÚSICA</label>
            <div className={`url-input-wrap ${err && !previewId ? 'url-input-error' : ''}`}>
              <Icon name="youtube" size={17} />
              <input id="youtube-link" value={input} onChange={(e) => { setInput(e.target.value); setError(null); }} onKeyDown={(e) => e.key === 'Enter' && startYoutube()} placeholder="Cole aqui o link do YouTube" inputMode="url" autoComplete="url" />
              {previewId && <span className="valid-input"><Icon name="checkCircle" size={17} /></span>}
            </div>
            {previewId ? (
              <div className="youtube-preview">
                <img src={meta?.thumbnail_url || youtubeThumb(previewId)} alt="Miniatura do vídeo" onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0'; }} />
                <div className="preview-play"><Icon name="play" size={16} /></div>
                <div className="preview-meta"><b>{meta?.title || 'Vídeo do YouTube'}</b><span>{meta?.author_name || 'Player oficial · sem extração de áudio'}</span></div>
              </div>
            ) : (
              <div className="youtube-hint"><Icon name="info" size={14} /><span>youtube.com, youtu.be e Shorts funcionam.</span></div>
            )}
            <button className="btn btn-primary card-action" onClick={startYoutube} disabled={!input.trim()}>
              <span>Usar esta música</span><Icon name="arrowRight" size={17} />
            </button>
            <div className="card-footnote"><Icon name="shield" size={13} /> Player oficial. Baixe apenas o vocal gravado.</div>
          </article>

          <article className="start-card file-card">
            <div className="card-topline"><span className="card-icon file-icon"><Icon name="wave" size={20} /></span><span className="card-step">02 / MEU INSTRUMENTAL</span></div>
            <h3>Traga sua própria base</h3>
            <p>Envie um áudio autorizado e grave, ajuste efeitos e exporte a mix completa.</p>
            <div className="file-format-row"><span>MP3</span><span>WAV</span><span>M4A</span><span>OGG</span><span>AAC*</span></div>
            <label className="rights-check">
              <input type="checkbox" checked={rights} onChange={(e) => { setRights(e.target.checked); setError(null); }} />
              <span className="custom-check"><Icon name="check" size={13} /></span>
              <span>Tenho autorização para usar este áudio.</span>
            </label>
            <input ref={fileRef} className="file-input-hidden" type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.aac" onChange={(e) => importFile(e.target.files?.[0])} />
            <button className="btn btn-secondary card-action" onClick={() => fileRef.current?.click()} disabled={!rights || fileBusy}>
              {fileBusy ? <><span className="button-spinner" /> Abrindo áudio…</> : <><Icon name="upload" size={17} /><span>Enviar instrumental</span><Icon name="arrowRight" size={17} className="button-arrow" /></>}
            </button>
            <div className="card-footnote"><Icon name="shield" size={13} /> O arquivo é processado no seu dispositivo. *Quando suportado.</div>
          </article>

          <article className="start-card quick-card">
            <div className="card-topline"><span className="card-icon quick-icon"><Icon name="mic" size={20} /></span><span className="card-step">03 / SEM BASE</span></div>
            <h3>Gravação rápida</h3>
            <p>Abra o microfone e capture uma ideia agora. Você pode adicionar uma base depois.</p>
            <div className="quick-wave" aria-hidden="true">{Array.from({ length: 42 }, (_, i) => <i key={i} style={{ height: `${8 + Math.abs(Math.sin(i * 1.4) * Math.cos(i * .18)) * 32}px` }} />)}</div>
            <div className="quick-spacer" />
            <button className="btn btn-outline card-action" onClick={startQuick}><Icon name="record" size={17} /><span>Começar sem instrumental</span><Icon name="arrowRight" size={17} className="button-arrow" /></button>
            <div className="card-footnote"><Icon name="mic" size={13} /> O microfone só é ativado quando você autorizar.</div>
          </article>
        </div>
        {err && <div className="inline-error"><Icon name="alert" size={16} /> {err}</div>}
      </section>

      <section className="steps-strip">
        <div className="steps-title"><span className="section-kicker">SEU FLUXO</span><b>Do primeiro play<br />ao arquivo final.</b></div>
        {[{n:'01',t:'Escolha a música',d:'YouTube ou sua base autorizada',icon:'youtube' as const},{n:'02',t:'Teste o microfone',d:'Ajuste o nível antes de cantar',icon:'mic' as const},{n:'03',t:'Grave seu take',d:'Pause, continue e regrave trechos',icon:'record' as const},{n:'04',t:'Ajuste e baixe',d:'Efeitos, mix e exportação local',icon:'export' as const}].map((step) => (
          <div className="flow-step" key={step.n}><span className="flow-icon"><Icon name={step.icon} size={17} /></span><div><small>{step.n}</small><b>{step.t}</b><span>{step.d}</span></div></div>
        ))}
      </section>

      <footer className="home-footer">
        <div className="footer-brand"><span className="brand-mark"><Icon name="logo" size={18} /></span><b>VOCAL<span>STUDIO</span></b><span className="footer-beta">BETA</span></div>
        <span>Seu áudio é processado localmente sempre que possível.</span>
        <div className="footer-links"><button onClick={onOpenProjects}>Meus projetos</button><span>·</span><span>v0.1 · Beta</span></div>
      </footer>
    </main>
  );
}
